/**
 * Shared editors for Brand Hub sections.
 * - SectionCard: bordered section with Save button
 * - ListEditor: array-of-strings editor (chips + add)
 * - RowsEditor: generic array-of-objects editor driven by a column config
 *
 * Iter 115: long-text (`multiline`) fields and `type:'textarea'` / `type:'richtext'`
 * columns now render the workspace-wide `SmartRichText` editor — 2-line default,
 * auto-expanding, floating toolbar on focus, optional media upload via
 * Emergent Object Storage.
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import SmartRichText, { SmartRichTextDisplay } from "@/components/SmartRichText";
import { useAuth } from "@/lib/auth";

/**
 * SectionCard — bordered section with an optional Save button.
 *
 * Auto-save (opt-in): pass `autoSave` (async function) + `dirtyKey` (any
 * value that changes when the section becomes dirty — usually a JSON.stringify
 * of the section's local state). Debounced 3s; a status pill shows in the
 * header ("Unsaved", "Saving…", "Saved · Xs ago", "Save failed").
 */
export function SectionCard({ title, icon: Icon, children, onSave, busy, testid, hint, autoSave, dirtyKey }) {
  const [autoStatus, setAutoStatus] = useState(null); // null | dirty | saving | saved | error
  const [savedAt, setSavedAt] = useState(null);
  const [savedAgo, setSavedAgo] = useState("");
  const lastKeyRef = useRef(dirtyKey);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!autoSave) return undefined;
    if (dirtyKey === lastKeyRef.current) return undefined;
    lastKeyRef.current = dirtyKey;
    setAutoStatus("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setAutoStatus("saving");
      try {
        await autoSave();
        setSavedAt(new Date());
        setAutoStatus("saved");
      } catch {
        setAutoStatus("error");
      }
    }, 3000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [dirtyKey, autoSave]);

  useEffect(() => {
    if (!savedAt) { setSavedAgo(""); return undefined; }
    const fmt = () => {
      const secs = Math.max(1, Math.round((Date.now() - savedAt.getTime()) / 1000));
      if (secs < 60) return `${secs}s ago`;
      const mins = Math.round(secs / 60);
      if (mins < 60) return `${mins}m ago`;
      return `${Math.round(mins / 60)}h ago`;
    };
    setSavedAgo(fmt());
    const id = setInterval(() => setSavedAgo(fmt()), 15000);
    return () => clearInterval(id);
  }, [savedAt]);

  return (
    <div className="border border-border" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-border bg-muted/40 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm font-semibold">{title}</span>
        {hint && <span className="text-[10px] text-muted-foreground ml-2">{hint}</span>}
        {autoStatus && (
          <span className="text-[10px] ml-auto flex items-center gap-1" data-testid={`${testid}-autosave-status`}>
            {autoStatus === "dirty" && <span className="text-amber-600 dark:text-amber-400">Unsaved</span>}
            {autoStatus === "saving" && (<><Loader2 className="w-3 h-3 animate-spin" /><span className="text-foreground/60">Saving…</span></>)}
            {autoStatus === "saved" && <span className="text-emerald-600 dark:text-emerald-400">Saved{savedAgo ? ` · ${savedAgo}` : ""}</span>}
            {autoStatus === "error" && <span className="text-destructive">Save failed</span>}
          </span>
        )}
        {onSave && (
          <Button size="sm" className={`rounded-none h-7 ${autoStatus ? "" : "ml-auto"}`} onClick={onSave} disabled={busy} data-testid={`${testid}-save`}>
            <Save className="w-3 h-3 mr-1.5" />{busy ? "Saving…" : "Save"}
          </Button>
        )}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

export function TextRow({ label, value, onChange, multiline, placeholder, testid, type = "text", richText = true, mediaMode = "upload", help, readMode = false }) {
  const { activeWorkspaceId } = useAuth();
  const useRich = multiline && richText;
  return (
    <div>
      <Label className="label-eyebrow mb-1 inline-flex items-center gap-1">
        <span>{label}</span>
        {help && (
          <span className="relative group inline-flex" data-testid={`${testid}-help`}>
            <svg className="w-3 h-3 text-foreground/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span className="pointer-events-none absolute left-full top-0 ml-2 hidden group-hover:block bg-foreground text-background text-[10px] normal-case tracking-normal px-2 py-1 whitespace-pre-line w-56 z-40 shadow-md">
              {help}
            </span>
          </span>
        )}
      </Label>
      {readMode ? (
        useRich ? (
          value ? <SmartRichTextDisplay html={value} testid={`${testid}-read`} /> : <span className="text-xs text-foreground/40 italic" data-testid={`${testid}-read`}>Not set</span>
        ) : (
          <div className="text-sm py-1" data-testid={`${testid}-read`}>{value || <span className="text-foreground/40 italic">Not set</span>}</div>
        )
      ) : useRich ? (
        <SmartRichText
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          mediaMode={mediaMode}
          workspaceId={activeWorkspaceId}
          testid={testid}
          help={help}
        />
      ) : multiline ? (
        <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={2} placeholder={placeholder} className="rounded-none text-sm" data-testid={testid} />
      ) : (
        <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-9 rounded-none text-sm" data-testid={testid} />
      )}
    </div>
  );
}

export function ListEditor({ label, items = [], onChange, placeholder = "Add item…", testid, readOnly = false }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  return (
    <div data-testid={testid}>
      {label && <Label className="label-eyebrow mb-1 inline-block">{label}</Label>}
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {(items || []).map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs border border-border px-2 py-1 bg-muted/30" data-testid={`${testid}-item-${i}`}>
            {it}
            {!readOnly && (
              <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-destructive" data-testid={`${testid}-del-${i}`}><Trash2 className="w-3 h-3" /></button>
            )}
          </span>
        ))}
        {readOnly && (items || []).length === 0 && (
          <span className="text-xs text-foreground/40 italic">Not set</span>
        )}
      </div>
      {!readOnly && (
        <div className="flex gap-1.5">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={placeholder} className="h-8 rounded-none text-xs" data-testid={`${testid}-input`} />
          <Button variant="outline" size="sm" className="rounded-none h-8" onClick={add} data-testid={`${testid}-add`}><Plus className="w-3 h-3" /></Button>
        </div>
      )}
    </div>
  );
}

/**
 * RowsEditor — columns: [{key, label, type: 'text'|'textarea'|'richtext'|'select'|'date'|'number'|'color', options?, width?, mediaMode?, rich?}]
 *
 * Notes:
 *   • `textarea` renders SmartRichText (mediaMode="url-only" by default — set
 *     `mediaMode="upload"` per-column to allow media uploads inside table cells).
 *   • `richtext` is an alias for `textarea` that stays explicit.
 *   • Set `rich: false` on a `textarea` column to fall back to plain <Textarea>.
 */
export function RowsEditor({ label, columns, rows = [], onChange, addLabel = "Add row", testid, readMode = false }) {
  const { activeWorkspaceId } = useAuth();
  const blank = () => Object.fromEntries(columns.map((c) => [c.key, c.type === "number" ? "" : ""]));
  const setCell = (i, key, val) => {
    const next = rows.map((r, j) => (j === i ? { ...r, [key]: val } : r));
    onChange(next);
  };
  return (
    <div data-testid={testid}>
      {label && <Label className="label-eyebrow mb-1.5 inline-block">{label}</Label>}
      <div className="space-y-2">
        {(rows || []).map((r, i) => (
          <div key={i} className="border border-border p-2.5 relative bg-muted/20" data-testid={`${testid}-row-${i}`}>
            {!readMode && (
              <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-destructive" data-testid={`${testid}-row-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pr-6">
              {columns.map((c) => {
                const isLong = c.type === "textarea" || c.type === "richtext";
                const useRich = isLong && c.rich !== false;
                if (readMode) {
                  return (
                    <div key={c.key} className={isLong ? "md:col-span-2" : ""}>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5 inline-block">{c.label}</Label>
                      {useRich && r[c.key] ? (
                        <SmartRichTextDisplay html={r[c.key]} testid={`${testid}-${i}-${c.key}-read`} />
                      ) : (
                        <div className="text-xs py-0.5" data-testid={`${testid}-${i}-${c.key}-read`}>{r[c.key] || <span className="text-foreground/40 italic">—</span>}</div>
                      )}
                    </div>
                  );
                }
                return (
                <div key={c.key} className={isLong ? "md:col-span-2" : ""}>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5 inline-block">{c.label}</Label>
                  {useRich ? (
                    <SmartRichText
                      value={r[c.key] ?? ""}
                      onChange={(v) => setCell(i, c.key, v)}
                      mediaMode={c.mediaMode || "url-only"}
                      workspaceId={activeWorkspaceId}
                      testid={`${testid}-${i}-${c.key}`}
                    />
                  ) : isLong ? (
                    <Textarea value={r[c.key] ?? ""} onChange={(e) => setCell(i, c.key, e.target.value)} rows={2} className="rounded-none text-xs" data-testid={`${testid}-${i}-${c.key}`} />
                  ) : c.type === "select" ? (
                    <Select value={r[c.key] || ""} onValueChange={(v) => setCell(i, c.key, v)}>
                      <SelectTrigger className="h-8 rounded-none text-xs" data-testid={`${testid}-${i}-${c.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{(c.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : c.type === "color" ? (
                    <div className="flex gap-1.5 items-center">
                      <Input type="color" value={r[c.key] || "#7c3aed"} onChange={(e) => setCell(i, c.key, e.target.value)} className="h-8 w-14 rounded-none p-0.5" data-testid={`${testid}-${i}-${c.key}`} />
                      <span className="text-xs font-mono">{r[c.key]}</span>
                    </div>
                  ) : (
                    <Input type={c.type === "number" ? "number" : c.type === "date" ? "date" : "text"} value={r[c.key] ?? ""} onChange={(e) => setCell(i, c.key, e.target.value)} className="h-8 rounded-none text-xs" data-testid={`${testid}-${i}-${c.key}`} />
                  )}
                </div>
                );
              })}
            </div>
          </div>
        ))}
        {readMode && (rows || []).length === 0 && (
          <div className="text-xs text-foreground/40 italic py-2">Nothing here yet.</div>
        )}
      </div>
      {!readMode && (
        <Button variant="outline" size="sm" className="rounded-none h-8 mt-2" onClick={() => onChange([...(rows || []), blank()])} data-testid={`${testid}-add`}>
          <Plus className="w-3 h-3 mr-1.5" />{addLabel}
        </Button>
      )}
    </div>
  );
}
